import { type FC } from 'react';
import { _isNotBlank, _isNotEmpty, _notNil } from '@/littledash';
import Indicator from '@/components/UI/Indicator';
import Tooltip from '@/components/UI/Tooltip';
import { centeredSlotIconStyle } from '@/components/Workflow/Show/ListLeadingSlot';
import { useWorkflowSetup } from '@/utils/workflow/useWorkflow.tsx';
import type { Animal } from '@/model/Animal.model';
import type { Study } from '@/model/Study.model';
import type { TreatmentGroup } from '@/model/TreatmentGroup.model';
import styles from './WorkflowAnimalIndicator.module.scss';

interface WorkflowAnimalIndicatorProps {
  study: Study;
  subject: Animal;
  hasAlert: boolean;
  testId?: string;
}

const WorkflowAnimalIndicator: FC<WorkflowAnimalIndicatorProps> = ({
  study,
  subject,
  hasAlert,
  testId = 'workflow-animal-indicator',
}) => {
  const { workflowSetup } = useWorkflowSetup();
  const showGroups = workflowSetup?.showGroups ?? false;
  const studyGroupId = subject.study_group_id;

  const studyGroup =
    showGroups && _isNotEmpty(study.study_groups) && _notNil(studyGroupId)
      ? study.study_groups.find((group: TreatmentGroup) => String(group.id) === String(studyGroupId))
      : undefined;

  if (_notNil(studyGroup)) {
    return (
      <>
        <Tooltip render={studyGroup.name}>
          <span
            className={styles.groupBadge}
            style={{ backgroundColor: _isNotBlank(studyGroup.color) ? studyGroup.color : '#333' }}
            data-testid={`${testId}-group`}
            aria-label={`${studyGroup.no} ${studyGroup.name}`}
          >
            {studyGroup.no}
          </span>
        </Tooltip>
        {hasAlert && (
          <span data-testid={`${testId}-badge`}>
            <Indicator smallIndicator indicatorClassName={styles.cornerAlert} />
          </span>
        )}
      </>
    );
  }

  if (hasAlert) {
    return (
      <span data-testid={`${testId}-alert`}>
        <Indicator smallIndicator indicatorStyle={centeredSlotIconStyle} />
      </span>
    );
  }

  return null;
};

export default WorkflowAnimalIndicator;
